/**
 * Pure Unassigned panel (SE-8391, Figma 355:3203 `organisms/UnassignedPanel`):
 * header pill · caption · rows · footnote. The header toggles the rows and
 * footnote (SE-8598, closed by default). Copy verbatim from 944:16235;
 * visual parity NOT-VERIFIED until that node binds (M02).
 * @module features/scouting/components/molecules/PitchUnassignedPanel
 */
import { ChevronDownIcon, ChevronRightIcon } from "lucide-react-native";
import React, { memo } from "react";

import { TonePill } from "@/components/atoms/Badge";
import { Box } from "@/components/atoms/Box";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import PitchUnassignedRow from "@/features/scouting/components/molecules/PitchUnassignedRow";

import type { PitchUnassignedPanelProps } from "./PitchUnassignedPanelContainer";

/** Footnote, verbatim from the frame. */
const FOOTNOTE =
  "Removing a player here removes them from the list / scenario; the trash icon on the left list does the same.";

/** Prepared props for the pure panel. */
export interface PitchUnassignedPanelViewProps extends PitchUnassignedPanelProps {
  readonly header: string;
  readonly caption: string;
  /** Whether the rows and footnote show (SE-8598: closed by default). */
  readonly expanded: boolean;
  readonly onToggle: () => void;
  /** "Show|Hide {n} unassigned player(s)" — the toggle's accessible name. */
  readonly toggleLabel: string;
}

/**
 * Renders the panel.
 * @param root0 - Prepared panel
 * @param root0.surface - Which pitch the panel sits under
 * @param root0.entries - One entry per Unassigned member, in rank order
 * @param root0.canDrag - Whether rows lift into the pitch DnD boundary
 * @param root0.onPlace - Place ▾ write; absent disables every trigger
 * @param root0.onRemove - Remove handler; absent = no Remove
 * @param root0.removeStateById - Where each removal stands
 * @param root0.header - "UNASSIGNED · {n}"
 * @param root0.caption - "No slot in {formation} · …"
 * @param root0.expanded - Whether the rows and footnote show
 * @param root0.onToggle - Flips the disclosure
 * @param root0.toggleLabel - The header toggle's accessible name
 * @returns The rendered panel
 */
const PitchUnassignedPanelView = ({
  surface,
  entries,
  canDrag,
  onPlace,
  onRemove,
  removeStateById,
  header,
  caption,
  expanded,
  onToggle,
  toggleLabel,
}: PitchUnassignedPanelViewProps) => (
  // SE-8486 (W78/S41, frame 971:20747): a neutral 640-wide box — px 24
  // py 12, gap 8, radius 12, the default outline on the base surface. The
  // warning tone lives on the header pill alone (W79); the box used to be
  // tinted warning edge to edge.
  <Box
    testID="pitch-unassigned-panel"
    accessibilityLabel="Unassigned panel"
    className="flex w-full max-w-pitch-unassigned-panel flex-col gap-2 rounded-lg border border-outline-default bg-surface-base px-6 py-3"
  >
    {/* SE-8598: the header row is the disclosure — the count stays readable
        while the rows are folded away under it. */}
    <Pressable
      testID="pitch-unassigned-toggle"
      onPress={onToggle}
      accessibilityRole="button"
      accessibilityLabel={toggleLabel}
      accessibilityState={{ expanded }}
      aria-expanded={expanded}
      className="flex-row flex-wrap items-center gap-2"
    >
      <Icon
        as={expanded ? ChevronDownIcon : ChevronRightIcon}
        size="sm"
        className="text-content-subtle"
      />
      {/* W79: micro-strong on the warning surface, px 8 py 4, radius 4 —
          the frame's TonePill, not the pill-shaped dot + text it was. */}
      <TonePill
        testID="pitch-unassigned-header"
        tone="warning-subtle"
        textVariant="micro-strong"
        className="rounded-sm px-2 py-1"
        label={header}
      />
      <Text
        testID="pitch-unassigned-caption"
        variant="caption"
        className="text-content-muted"
      >
        {caption}
      </Text>
    </Pressable>
    {expanded ? (
      <Stack testID="pitch-unassigned-rows" className="gap-2">
        {entries.map(entry => (
          <PitchUnassignedRow
            key={entry.playerId}
            surface={surface}
            entry={entry}
            canDrag={canDrag}
            onPlace={onPlace}
            onRemove={onRemove}
            removeState={removeStateById?.[entry.playerId]}
          />
        ))}
      </Stack>
    ) : null}
    {expanded && onRemove ? (
      <Text
        testID="pitch-unassigned-footnote"
        variant="micro"
        className="text-content-muted"
      >
        {FOOTNOTE}
      </Text>
    ) : null}
  </Box>
);

PitchUnassignedPanelView.displayName = "PitchUnassignedPanelView";
export default memo(PitchUnassignedPanelView);
